import React, { useState } from "react";
import { motion } from "framer-motion";

const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .dr-root {
    background: #f5f5f7;
    color: #1d1d1f;
    min-height: 100vh;
    font-family: Arial, Helvetica, sans-serif;
    -webkit-font-smoothing: antialiased;
    padding-top: 184px;
  }

  /* ── HERO desktop ── */
  .dr-hero {
    width: 90%;
    height: 55vh;
    min-height: 340px;
    position: relative;
    overflow: hidden;
    margin: 0 auto;
    border-radius: 20px;
  }
  .dr-hero img {
    width: 100%; height: 100%;
    object-fit: cover;
    filter: brightness(0.46) saturate(0.8);
    transform: scale(1.04);
    transition: transform 8s ease;
    border-radius: 20px;
  }
  .dr-hero-vignette {
    position: absolute; inset: 0;
    background:
      linear-gradient(to top, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0) 50%, transparent 100%),
      linear-gradient(to right, rgba(0,0,0,0.32) 0%, transparent 55%);
  }
  .dr-hero-text {
    position: absolute; bottom: 0; left: 0; right: 0;
    padding: clamp(2rem, 6vw, 4.5rem);
  }
  .dr-hero-kicker {
    font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase;
    color: #60a5fa; margin-bottom: 0.9rem; display: flex; align-items: center; gap: 10px;
  }
  .dr-hero-kicker::before {
    content: ''; display: block;
    width: 28px; height: 1.5px; background: #3b82f6;
  }
  .dr-hero-title {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(2.8rem, 7.5vw, 6rem);
    font-weight: 700; color: #fff; line-height: 0.92;
    letter-spacing: -0.03em;
  }
  .dr-hero-title em { font-style: italic; color: #60a5fa; }
  .dr-hero-sub {
    font-size: clamp(0.78rem, 1.3vw, 0.9rem);
    color: rgba(255,255,255,1); margin-top: 0.9rem;
    max-width: 460px; line-height: 1.68;
  }

  /* ── HERO mobile ── */
  @media (max-width: 600px) {
    .dr-root {
      padding-top: 0;
    }
    .dr-hero {
      width: 94%;
      height: 200px;
      min-height: unset;
      border-radius: 16px;
      margin: 120px auto 0;
    }
    .dr-hero img {
      border-radius: 16px;
      filter: brightness(0.50) saturate(0.85);
    }
    .dr-hero-text {
      padding: 0.85rem 1rem 1rem;
    }
    .dr-hero-kicker {
      font-size: 7.5px;
      letter-spacing: 0.16em;
      margin-bottom: 0.35rem;
      gap: 6px;
    }
    .dr-hero-kicker::before {
      width: 14px;
      height: 1px;
    }
    .dr-hero-sub {
      font-size: 0.7rem;
      margin-top: 0.28rem;
      line-height: 1.52;
      max-width: 100%;
      color: rgba(255,255,255,0.92);
    }
  }

  /* ── BOLD FOUNDATION ── */
  .dr-foundation {
    background: #fff;
    border-top: 0.5px solid rgba(29,29,31,0.07);
    border-bottom: 0.5px solid rgba(29,29,31,0.07);
    padding: clamp(3rem, 6vw, 5rem) clamp(1.5rem, 6vw, 4.5rem);
    position: relative;
    overflow: hidden;
    margin-top: clamp(2rem, 4vw, 3rem);
  }
  .dr-foundation::before {
    content: 'FOUNDATION';
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'Big Caslon','Book Antiqua','Palatino Linotype',Georgia,serif;
    font-size: clamp(5rem, 14vw, 13rem);
    font-weight: 400; color: rgba(29,29,31,0.035);
    white-space: nowrap; pointer-events: none;
    letter-spacing: -0.04em; line-height: 1;
  }
  .dr-foundation-inner {
    max-width: 1080px; margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: clamp(2rem, 5vw, 5rem);
    align-items: start;
    position: relative; z-index: 1;
  }
  @media (max-width: 680px) {
    .dr-foundation-inner { grid-template-columns: 1fr; gap: 1.5rem; }
  }
  .dr-foundation-label {
    font-size: 9px; letter-spacing: 0.26em; text-transform: uppercase;
    color: #3b82f6; margin-bottom: 1rem; display: flex; align-items: center; gap: 8px;
  }
  .dr-foundation-label::before {
    content: ''; display: block;
    width: 20px; height: 1px; background: #3b82f6;
  }
  .dr-foundation-heading {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(2rem, 4vw, 3.4rem);
    font-weight: 700; color: #1d1d1f;
    line-height: 1.0; letter-spacing: -0.025em;
  }
  .dr-foundation-heading em { font-style: italic; color: #2563eb; }
  .dr-foundation-refs {
    display: flex; flex-wrap: wrap; gap: 6px; margin-top: 1.5rem;
  }
  .dr-foundation-ref {
    font-size: 8px; letter-spacing: 0.14em; text-transform: uppercase;
    color: #3b82f6; border: 0.5px solid rgba(59,130,246,0.22);
    background: rgba(59,130,246,0.05);
    padding: 4px 9px; border-radius: 3px;
  }
  .dr-foundation-body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(1.1rem, 1.9vw, 1.4rem);
    line-height: 1.72; color: #3a3a3c;
    border-left: 2px solid #2563eb;
    padding-left: 1.5rem;
  }
  .dr-foundation-body strong {
    color: #1d1d1f; font-weight: 400; font-style: normal;
  }
  .dr-foundation-divider {
    max-width: 1080px; margin: 2.5rem auto 0;
    height: 0.5px; background: rgba(29,29,31,0.07);
    position: relative; z-index: 1;
  }

  /* ── LIST WRAPPER ── */
  .dr-list {
    max-width: 1080px;
    margin: 0 auto;
    padding: 4rem clamp(1rem, 5vw, 3rem) 8rem;
  }
  .dr-list-head {
    display: flex; align-items: center; gap: 1.25rem; margin-bottom: 2.8rem;
  }
  .dr-list-head-title {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(1.7rem, 3.5vw, 2.8rem);
    font-weight: 700; color: #1d1d1f;
    letter-spacing: -0.02em; line-height: 1; white-space: nowrap;
  }
  .dr-list-head-title em { font-style: italic; color: #2563eb; }
  .dr-list-head-line { flex: 1; height: 0.5px; background: rgba(29,29,31,0.10); }
  .dr-list-head-count {
    font-size: 9px; letter-spacing: 0.18em;
    text-transform: uppercase; color: rgba(29,29,31,0.30); white-space: nowrap;
  }

  /* ── CARD ── */
  .dr-card { border-bottom: 0.5px solid rgba(29,29,31,0.09); }
  .dr-card:first-of-type { border-top: 0.5px solid rgba(29,29,31,0.09); }

  .dr-row {
    display: grid;
    grid-template-columns: 52px 1fr 200px 38px;
    align-items: center;
    gap: 1.25rem;
    width: 100%; border: none; background: transparent;
    text-align: left; cursor: pointer;
    padding: 0; transition: background 0.2s;
  }
  .dr-row:hover { background: rgba(59,130,246,0.028); }
  .dr-row:hover .dr-card-title { color: #2563eb; }
  .dr-row:hover .dr-thumb img { transform: scale(1.07); filter: brightness(0.78); }

  @media (max-width: 660px) {
    .dr-row { grid-template-columns: 40px 1fr 36px; }
    .dr-thumb { display: none !important; }
  }

  .dr-num {
    font-size: 10px; letter-spacing: 0.12em;
    color: #3b82f6; padding: 1.5rem 0;
    font-variant-numeric: tabular-nums; text-align: right;
  }
  .dr-card-info { padding: 1.5rem 0; min-width: 0; }
  .dr-card-title {
    font-family: Arial, Helvetica, sans-serif;
    font-size: clamp(1.05rem, 1.9vw, 1.36rem);
    font-weight: 700; color: #1d1d1f;
    letter-spacing: -0.01em; line-height: 1.2;
    transition: color 0.22s ease;
  }
  .dr-card-peek {
    font-size: 0.75rem; color: rgba(29,29,31,0.36);
    margin-top: 0.28rem; line-height: 1.5;
    display: -webkit-box; -webkit-line-clamp: 1;
    -webkit-box-orient: vertical; overflow: hidden;
  }

  .dr-thumb {
    width: 200px; height: 62px;
    border-radius: 7px; overflow: hidden;
    background: #e5e5ea; flex-shrink: 0;
  }
  .dr-thumb img {
    width: 100%; height: 100%; object-fit: cover;
    filter: brightness(0.90) saturate(0.85);
    transition: transform 0.55s cubic-bezier(0.25,0.46,0.45,0.94), filter 0.35s ease;
  }

  .dr-chev {
    width: 34px; height: 34px; border-radius: 50%;
    border: 0.5px solid rgba(29,29,31,0.11);
    background: #fff; color: #3b82f6;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: all 0.32s cubic-bezier(0.16,1,0.3,1);
  }
  .dr-chev.open {
    background: #2563eb; border-color: #2563eb;
    color: #fff; transform: rotate(180deg);
  }
  .dr-chev svg { width: 13px; height: 13px; }

  /* ── PANEL ── */
  .dr-panel {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.46s cubic-bezier(0.4,0,0.2,1);
  }
  .dr-panel.open { grid-template-rows: 1fr; }
  .dr-panel-inner { overflow: hidden; }
  .dr-panel-body {
    padding: 0.5rem 0 3rem 0;
    padding-left: calc(52px + 1.25rem);
  }
  @media (max-width: 660px) { .dr-panel-body { padding-left: calc(40px + 1rem); } }

  .dr-detail {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 2.5rem;
    align-items: start;
  }
  @media (max-width: 780px) {
    .dr-detail { grid-template-columns: 1fr; }
  }

  .dr-detail-img-wrap {
    width: 320px; max-width: 100%;
    border-radius: 14px; overflow: hidden;
    position: relative; background: #d1d1d6;
    aspect-ratio: 4 / 3; flex-shrink: 0;
  }
  @media (max-width: 780px) { .dr-detail-img-wrap { width: 100%; } }
  .dr-detail-img-wrap img {
    width: 100%; height: 100%; object-fit: cover;
    filter: brightness(0.82) saturate(0.88);
    transform: scale(1.04);
    transition: transform 0.65s cubic-bezier(0.25,0.46,0.45,0.94);
    display: block;
  }
  .dr-panel.open .dr-detail-img-wrap img { transform: scale(1); }
  .dr-detail-img-vignette {
    position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.52) 0%, transparent 55%);
  }
  .dr-detail-img-label {
    position: absolute; bottom: 0.9rem; left: 1rem;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 1.1rem; font-weight: 700; color: #fff;
    letter-spacing: -0.01em; pointer-events: none; line-height: 1.2;
  }
  .dr-detail-img-label em { font-style: italic; color: #93c5fd; }

  .dr-detail-text {}
  .dr-detail-text p {
    font-size: 0.875rem; line-height: 1.78;
    color: #3a3a3c; margin-bottom: 0.85rem;
  }
  .dr-detail-text p:last-child { margin-bottom: 0; }

  .dr-scripture {
    margin-top: 1.5rem;
    display: flex; gap: 1rem; align-items: flex-start;
    padding: 0.9rem 1.1rem;
    background: rgba(59,130,246,0.04);
    border-left: 2px solid #3b82f6;
    border-radius: 0 7px 7px 0;
  }
  .dr-scripture-dot {
    width: 4px; height: 4px; border-radius: 50%;
    background: #3b82f6; margin-top: 5px; flex-shrink: 0;
  }
  .dr-scripture-label {
    font-size: 8px; letter-spacing: 0.22em; text-transform: uppercase;
    color: #3b82f6; display: block; margin-bottom: 0.35rem;
  }
  .dr-scripture-text {
    font-size: 0.76rem; line-height: 1.65; color: rgba(29,29,31,0.44);
  }

  @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
const accordioni = [
  {
    question: "BCS Uniform",
    answer: `The official uniform of the Brotherhood of the Cross and Star is a white garment with cape known officially as the "soutane" — in conformity with what is recorded in the Revelation of John the Divine.`,
    a: `"…these are they which came out of great tribulation, and have washed their robes, and made them white in the blood of the lamb. Therefore are they before the throne of God, and serve him day and night in his temple." — Revelation 7:13-15`,
    b: `"And to her was granted that she should be arrayed in fine linen, clean and white; for the fine linen is the righteousness of saints." — Revelation 19:8. Brothers and sisters use the same uniform; sisters add white head ties.`,
    o: `Ecclesiastes 9:8 · Matthew 17:2 · Luke 9:29 · Mark 16:5 · Acts 1:10 · Revelation 3:4–5, 3:18, 4:4, 6:11, 7:9, 7:13 · Revelation 19:14`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2FUniform.jpg?alt=media&token=0ee50ac7-99df-492e-bfc3-41aace3978fe",
  },
  {
    question: "Knocking of Head",
    answer: `BCS members knock (bow) their head three times in reverence to the Holy Trinity God — before and after prayers, or at any time, place, or occasion whenever the spirit directs.`,
    a: `Knocking of the head is a vital part of communication with God; it is akin to a dialing tone or knocking on the door before going in.`,
    o: `Revelation 4:10-11 · Revelation 19:4 · 1 Corinthians 14:25 · Revelation 7:11`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Fkoh.jpg?alt=media&token=b356e652-565a-4074-bf9e-3e8dd8f80f79",
  },
  {
    question: "Wearing of Shoes",
    answer: `It is the doctrine of the Brotherhood of the Cross and Star not to wear shoes into the Bethels or house of worship — in recognition and reverence of the holiness and presence of God.`,
    a: `"Put off thy shoes from thy feet; for the place where thou standest is Holy ground." God is holy and all worshippers must worship Him in spirit and in truth.`,
    o: `Acts 7:33 · Exodus 3:5 · Romans 10:15 · Luke 22:35 · Luke 10:4 · Luke 9:3`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Fshoes.jpg?alt=media&token=fc0d611f-cb87-4f80-8814-cf1dcb67667f",
  },
  {
    question: "Greetings",
    answer: `"Peace of the Father", "Perfect Peace", or "Peace be unto you" is the universal greeting of BCS members — irrespective of time, place, or occasion.`,
    a: `Our Lord Jesus Christ is the Prince of Peace. He used the same greeting and his disciples followed suit in evangelism and after His ascension. Peace is the language of heaven.`,
    o: `Luke 10:5 · John 20:19, 26 · Matthew 10:12-13 · Isaiah 9:6 · Luke 24:36 · John 14:27 · Philippians 4:7-9`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2FGreetings.jpg?alt=media&token=607618c5-3c17-4bae-98ce-e874076b9de7",
  },
  {
    question: "Kneeling in Reverence",
    answer: `Kneeling before God is an act of deep reverence and humility — a physical expression of submission and worship observed during prayers and services in Brotherhood of the Cross and Star.`,
    o: `1 Kings 8:54 · 2 Chronicles 6:13 · Daniel 6:10 · Acts 9:40 · Acts 20:36 · Psalm 95:6 · Philippians 2:9-11 · Mark 10:17 · Luke 22:41 · Acts 7:60`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Fkneel.jpg?alt=media&token=c7a78bc0-1bc2-4044-b414-cdcff3288d8d",
  },
  {
    question: "Tithe Payment",
    answer: `Everybody must pay one tenth of his/her earnings as the tithe of God. Bethels, Associations, and fellowships must also pay tithe from proceeds. Tithe is a divine covenant between you and your God.`,
    a: `"Bring ye all the tithe into the storehouse… and I will open you the windows of heaven and pour you out a blessing that there shall not be room enough to receive it." — Malachi 3:10`,
    b: `You attract great blessings when you pay your tithe regularly. Neglecting this ordinance invites spiritual and material consequences, according to the word of the LORD of hosts.`,
    o: `Malachi 3:6-12 · Luke 18:12-14 · Leviticus 27:30`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Ftie.jpg?alt=media&token=bf95933b-caeb-4bea-b210-7377251aeb38",
  },
  {
    question: "Evangelism",
    answer: `Evangelism is the divine mandate and commission to spread the gospel — a show of love and selflessness, striving for peace and togetherness, paving the way for the return of the lost sheep.`,
    a: `Open-air Evangelism: conducted in markets and village squares. Door-to-Door: brethren move house to house administering the gospel in localized areas.`,
    b: `Media Evangelism: through StarCross TV, the Official Website, and The New Kingdom Trumpet. Lifestyle Evangelism: "Let your light so shine before men, that they may see your good works." (Matt. 5:14–16)`,
    o: `2 Timothy 4:5 · Acts 2:38 · Mark 16:15-17 · Matthew 28:19-20 · Matthew 9:37-38 · Matthew 10:7-14`,
    image: "https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Feva.jpg?alt=media&token=70761a90-695e-46e4-8904-3013b17c8904",
  },
];

// ─── Card ─────────────────────────────────────────────────────────────────────
function DocCard({ data, index, isOpen, onToggle }) {
  const keys  = ["answer","a","b","c","d","g","h","i","j","k","l","m","n"];
  const paras = keys.map(k => data[k]).filter(Boolean);

  const words = data.question.split(" ");
  const labelStart = words.slice(0, -1).join(" ");
  const labelEnd   = words.slice(-1)[0];

  return (
    <div className="dr-card">
      <button className="dr-row" onClick={onToggle} aria-expanded={isOpen}>
        <span className="dr-num">{String(index + 1).padStart(2, "0")}</span>
        <div className="dr-card-info">
          <p className="dr-card-title">{data.question}</p>
          <p className="dr-card-peek">{data.answer}</p>
        </div>
        {data.image && (
          <div className="dr-thumb">
            <img src={data.image} alt={data.question} loading="lazy" />
          </div>
        )}
        <div className={`dr-chev ${isOpen ? "open" : ""}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>

      <div className={`dr-panel ${isOpen ? "open" : ""}`}>
        <div className="dr-panel-inner">
          <div className="dr-panel-body">
            <div className="dr-detail">
              {data.image && (
                <div className="dr-detail-img-wrap">
                  <img src={data.image} alt={data.question} loading="lazy" />
                  <div className="dr-detail-img-vignette" />
                  <div className="dr-detail-img-label">
                    {labelStart && <>{labelStart} </>}
                    <em>{labelEnd}</em>
                  </div>
                </div>
              )}
              <div className="dr-detail-text">
                {paras.map((p, i) => <p key={i}>{p.trim()}</p>)}
                {data.o && (
                  <div className="dr-scripture">
                    <div className="dr-scripture-dot" />
                    <div>
                      <span className="dr-scripture-label">Scripture References</span>
                      <p className="dr-scripture-text">{data.o}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
function ADoctrine() {
  const [open, setOpen] = useState(null);
  const toogle = (index) => setOpen(prev => prev === index ? null : index);

  return (
    <>
      <style>{CSS}</style>
      <motion.div
        className="dr-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      >

        {/* ── HERO ── */}
        <div className="dr-hero">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2FDoctrine.jpg?alt=media&token=4aae6249-896f-4916-9d53-a38e8988b162"
            alt="BCS Doctrine"
          />
          <div className="dr-hero-vignette" />
          <div className="dr-hero-text">
            <span className="dr-hero-kicker">Brotherhood of the Cross and Star</span>
            {/* <h1 className="dr-hero-title">Our <em>Doctrines</em></h1> */}
            <p className="dr-hero-sub">
              All doctrines derive from the Supernatural Teachings of Our Lord and
              Saviour Jesus Christ — truth, peace, love divine, forgiveness, and purity.
            </p>
          </div>
        </div>

        {/* ── BOLD FOUNDATION ── */}
        <div className="dr-foundation">
          <div className="dr-foundation-inner">
            <div className="dr-foundation-left">
              <p className="dr-foundation-label">Foundation of Faith</p>
              <h2 className="dr-foundation-heading">
                The<br /><em>Word</em><br />Stands
              </h2>
              <div className="dr-foundation-refs">
                {["Isa. 2:2-4", "John 16:12-15", "1 Cor. 13:10-12"].map(r => (
                  <span key={r} className="dr-foundation-ref">{r}</span>
                ))}
              </div>
            </div>
            <div className="dr-foundation-right">
              <p className="dr-foundation-body">
                Brotherhood Of The Cross And Star derives <strong>all her doctrines</strong> from
                the Supernatural Teachings and Doctrines Of Our Lord and Saviour,{" "}
                <strong>Our Lord Jesus Christ</strong>. These sacred ordinances are not of man,
                but of the Father — living instructions for worship, conduct, and fellowship
                that align every member with the truth, peace, and purity of God's kingdom.
              </p>
            </div>
          </div>
          <div className="dr-foundation-divider" />
        </div>

        {/* ── DOCTRINE LIST ── */}
        <div className="dr-list">
          <div className="dr-list-head">
            <h2 className="dr-list-head-title">BCS <em>Doctrines</em></h2>
            <div className="dr-list-head-line" />
            {/* <span className="dr-list-head-count">{accordioni.length} Doctrines</span> */}
          </div>

          {accordioni.map((data, index) => (
            <DocCard
              key={index}
              data={data}
              index={index}
              isOpen={open === index}
              onToggle={() => toogle(index)}
            />
          ))}
        </div>

      </motion.div>
    </>
  );
}

export default ADoctrine;
